import {
  ComboboxMulti,
  ComboboxMultiButtonTrigger,
  ComboboxMultiCollection,
  ComboboxMultiContent,
  ComboboxMultiEmpty,
  ComboboxMultiGroup,
  ComboboxMultiItem,
  ComboboxMultiLabel,
  ComboboxMultiList,
  ComboboxMultiSearchInput,
} from '@crono-one/design-system';
import cn from 'clsx';
import { ExternalProperty } from 'crono-fe-common/types/externalProperty';
import ExternalPropertyFilter from 'crono-fe-common/types/externalPropertyFilter';
import {
  getExternalPropertyLabels,
  getExternalPropertyOptions,
} from 'crono-fe-common/utils';
import { useMemo } from 'react';

/**
 * One option of one tag property. The property id travels with the option because
 * two properties can offer the same value, and the filter has to know which one a
 * pick belongs to.
 */
interface TagOption {
  externalPropertyId: number;
  value: string;
  label: string;
}

interface TagOptionGroup {
  //Base UI reads the group's heading off `value`
  value: string;
  items: TagOption[];
}

interface SequenceTagsFilterProps {
  //Fetched by the host: React Query in the app, IPC in the extension
  externalProperties: ExternalProperty[];
  filterExternalProperties: ExternalPropertyFilter[];
  setFilterExternalProperties: (value: ExternalPropertyFilter[]) => void;
}

const SequenceTagsFilter = ({
  externalProperties,
  filterExternalProperties,
  setFilterExternalProperties,
}: SequenceTagsFilterProps) => {
  const groups = useMemo<TagOptionGroup[]>(() => {
    const tagProperties = externalProperties.filter(
      (property) => property.isTag,
    );

    return tagProperties
      .map((property) => {
        const options = getExternalPropertyOptions(property) ?? [];
        const labels = getExternalPropertyLabels(property);

        return {
          value: property.publicName ?? '',
          items: options.map((option, index) => ({
            externalPropertyId: property.id,
            value: option,
            label: labels.at(index) ?? option,
          })),
        };
      })
      .filter((group) => group.items.length > 0);
  }, [externalProperties]);

  const selected = useMemo<TagOption[]>(() => {
    const optionsById = new Map<number, TagOption[]>();
    groups.forEach((group) =>
      group.items.forEach((option) => {
        const existing = optionsById.get(option.externalPropertyId) ?? [];
        optionsById.set(option.externalPropertyId, [...existing, option]);
      }),
    );

    return filterExternalProperties.flatMap((filter) =>
      (filter.values ?? []).map(
        (value) =>
          optionsById
            .get(filter.externalPropertyId)
            ?.find((option) => option.value === value) ?? {
            externalPropertyId: filter.externalPropertyId,
            value,
            label: value,
          },
      ),
    );
  }, [filterExternalProperties, groups]);

  const handleValueChange = (options: TagOption[]) => {
    const valuesById = new Map<number, string[]>();
    options.forEach((option) => {
      const existing = valuesById.get(option.externalPropertyId) ?? [];
      valuesById.set(option.externalPropertyId, [...existing, option.value]);
    });

    setFilterExternalProperties(
      Array.from(valuesById.entries()).map(([externalPropertyId, values]) => ({
        externalPropertyId,
        values,
      })) as ExternalPropertyFilter[],
    );
  };

  if (!groups.length) {
    return null;
  }

  return (
    <ComboboxMulti<TagOption>
      items={groups}
      value={selected}
      onValueChange={handleValueChange}
      itemToStringLabel={(option) => option.label}
      isItemEqualToValue={(option, value) =>
        option.externalPropertyId === value.externalPropertyId &&
        option.value === value.value
      }
    >
      {/* role="combobox" takes no name from its content, so it needs a label of
          its own to be reachable by name. The trigger's built-in label counts
          the picks against `items`, which here is one entry per property rather
          than per option, so it is replaced with a count of its own */}
      <ComboboxMultiButtonTrigger
        size="sm"
        aria-label="Tags"
        className="w-27.5 shrink-0"
      >
        <span
          className={cn(
            'truncate',
            selected.length ? 'text-gray-900' : 'text-gray-700',
          )}
        >
          {selected.length ? `${selected.length} selected` : 'Tags'}
        </span>
      </ComboboxMultiButtonTrigger>

      {/* The popup defaults to the trigger's width; tag names are far wider than
          the 110px trigger, so it is allowed to size to its content instead */}
      <ComboboxMultiContent
        positionerClassname="z-50000"
        className="w-auto min-w-55 max-w-80"
      >
        <ComboboxMultiSearchInput placeholder="Search tags..." />
        <ComboboxMultiEmpty>No tags found.</ComboboxMultiEmpty>

        <ComboboxMultiList>
          {(group: TagOptionGroup) => (
            <ComboboxMultiGroup key={group.value} items={group.items}>
              <ComboboxMultiLabel>{group.value}</ComboboxMultiLabel>
              <ComboboxMultiCollection>
                {(option: TagOption) => (
                  <ComboboxMultiItem
                    key={`${option.externalPropertyId}-${option.value}`}
                    value={option}
                  >
                    {option.label}
                  </ComboboxMultiItem>
                )}
              </ComboboxMultiCollection>
            </ComboboxMultiGroup>
          )}
        </ComboboxMultiList>
      </ComboboxMultiContent>
    </ComboboxMulti>
  );
};

export default SequenceTagsFilter;
